import type { HelpArticle } from "../../types";
import { DEAD_GRAVITY_HELP_FIXTURE, RAIN_ICE_HELP_FIXTURE } from "../../../calculations/calcPreviewFixtures";
import { LOAD_CALC_TEMPLATE_BY_ID } from "../../../calculations/loadCalcDefinitions";

const dead = DEAD_GRAVITY_HELP_FIXTURE;
const rain = RAIN_ICE_HELP_FIXTURE;
const deadCaseCount = LOAD_CALC_TEMPLATE_BY_ID.dead_gravity.cases.length;

export const loadCalcResultsArticle: HelpArticle = {
  id: "load-calc-results",
  title: "Reading load-calculation results",
  description:
    "Read status, governing value, trace, tables, assumptions, deferred work, and sources without mistaking a load generator for a member design check.",
  tags: ["load result", "status", "governing", "trace", "partial", "sources"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A manual load result is a structured record, not a green-or-red member check. Start with its status, then verify the governing label and value against the result table and trace. Finish with warnings, assumptions, deferred work, notes, and sources. That sequence separates what the engine calculated from what still needs engineering review.",
    },

    { kind: "heading", level: 2, text: "Start with status and governing", id: "status-and-governing" },
    { kind: "where", path: ["Calculations", "Project", "Load sheet", "Results"] },
    {
      kind: "paragraph",
      text: `The summary strip is an index into the record. On ${dead.mark}, **Status** is Calculated, **Governing** is ${dead.result.governingLabel}, and **Value** is ${dead.result.governingValue}. **Cases** is ${deadCaseCount} because the dead-gravity template defines Roof, Floor, Deck, and Ceiling cases; it is a template count, not the number of table rows. **Source** says Engine result when a result payload is present.`,
    },
    {
      kind: "shot",
      shotId: "load-result-summary",
      caption: `${dead.mark}'s summary: Calculated, ${dead.result.governingLabel}, ${dead.result.governingValue}, and the template's ${deadCaseCount} cases.`,
    },
    {
      kind: "definition-list",
      items: [
        { term: "Complete", definition: "The calculator produced its requested load record. The workspace displays this as Calculated." },
        { term: "Partial", definition: "Some values are computed, while the result explicitly names a deferred or review-required part." },
        { term: "Blocked", definition: "The calculator could not produce the requested record from the supplied input packet." },
        { term: "Not applicable", definition: "The requested phenomenon does not apply under the supplied applicability inputs." },
        { term: "Draft", definition: "No current stored result is available for the sheet." },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Complete does not mean the project is complete",
      body: "Complete describes this calculator's result contract. It does not approve the selected procedure, establish that every required load case exists, or turn the output into a member-capacity verdict.",
    },

    { kind: "heading", level: 2, text: "Reconcile the number", id: "reconcile-number" },
    {
      kind: "paragraph",
      text: `${dead.mark} is a deterministic dead-gravity example. Its entered component weights are ${dead.inputs.structure_psf}, ${dead.inputs.finish_psf}, ${dead.inputs.ceiling_psf}, ${dead.inputs.mep_psf}, and ${dead.inputs.partition_psf} psf. The engine totals them to ${dead.result.tables["gravity-regions"][0].dead.toFixed(2)} psf. Its ${dead.inputs.length_ft} ft by ${dead.inputs.width_ft} ft region is ${dead.result.tables["gravity-regions"][0].area} ft², producing ${dead.result.tables["gravity-regions"][0].weight} lb.`,
    },
    {
      kind: "formula",
      expression: `${dead.inputs.structure_psf} + ${dead.inputs.finish_psf} + ${dead.inputs.ceiling_psf} + ${dead.inputs.mep_psf} + ${dead.inputs.partition_psf} = ${dead.result.tables["gravity-regions"][0].dead.toFixed(2)} psf; ${dead.inputs.length_ft} × ${dead.inputs.width_ft} = ${dead.result.tables["gravity-regions"][0].area} ft²; ${dead.result.tables["gravity-regions"][0].dead.toFixed(2)} × ${dead.result.tables["gravity-regions"][0].area} = ${dead.result.tables["gravity-regions"][0].weight} lb`,
      caption: "The fixture's input-to-result chain. Every value also appears in the captured workspace.",
    },
    {
      kind: "values",
      caption: `${dead.mark}: the three values to reconcile across summary, trace, and table.`,
      rows: [
        { label: "Assembly dead load", value: dead.result.governingValue },
        { label: "Manual region area", value: `${dead.result.tables["gravity-regions"][0].area} ft²` },
        { label: "Total region weight", value: `${dead.result.tables["gravity-regions"][0].weight} lb` },
      ],
    },
    {
      kind: "paragraph",
      text: "The trace is the compact chain of engine intermediates. The result table is the row-level deliverable. A trustworthy review reaches the same governing value through both. If a summary is plausible but the row label, area, source mode, or intermediate does not describe the intended region, correct the inputs and recalculate rather than accepting the headline.",
    },
    {
      kind: "shot",
      shotId: "load-result-trace",
      caption: `${dead.mark}'s engine trace preserves the assembly total, region area, total weight, routing mode, and seismic-weight treatment behind the headline.`,
    },

    { kind: "heading", level: 2, text: "Treat Partial as an explicit boundary", id: "partial-results" },
    {
      kind: "paragraph",
      text: `${rain.mark} shows the intended distinction. The rain calculation returns **Partial** and ${rain.result.governingValue} for ${rain.result.governingLabel}. Its environmental table marks the rain row computed, the ponding row review required, and atmospheric ice not applicable. The deferred disclosure states that structural ponding stability and drainage capacity require a separate system check. The ${rain.result.governingValue} rain value is usable only with that boundary attached.`,
    },
    {
      kind: "shot",
      shotId: "load-result-partial",
      caption: `${rain.mark}: a computed ${rain.result.governingValue} rain row remains Partial because ponding is review required and a separate system check is deferred.`,
    },
    {
      kind: "paragraph",
      text: "A partial record should travel with its disclosures. Do not copy the governing number into another document and leave behind the deferred line that qualifies it. Likewise, a not-applicable row is a recorded outcome, not missing data; a blank value paired with that status means the phenomenon was screened out under the supplied applicability input.",
    },

    { kind: "heading", level: 2, text: "Read the whole result contract", id: "result-contract" },
    {
      kind: "table",
      caption: "Fields returned by the load-result contract and how to review them.",
      headers: ["Part", "Review question"],
      rows: [
        ["Status", "Did the engine complete, partially complete, block, or screen out the requested calculation?"],
        ["Governing label and value", "Which named row or phenomenon controls, in what displayed units?"],
        ["Tables", "Do the row labels, cases, units, and source columns match the modeled scope?"],
        ["Intermediates", "Can the governing value be reconciled to the important transformations?"],
        ["Warnings", "What condition did the engine flag without refusing the run?"],
        ["Assumptions", "What interpretation did the calculator make to finish the record?"],
        ["Deferred", "Which required review or system check remains outside this result?"],
        ["Notes", "What additional engine-authored context accompanies the result?"],
        ["Sources", "Which standards and implementation sources does this result identify?"],
      ],
    },
    {
      kind: "shot",
      shotId: "load-result-provenance",
      caption: `${dead.mark}'s clean disclosure set: the entered-load assumption and its returned standard and manual-input sources, without machine-specific paths.`,
    },
    {
      kind: "paragraph",
      text: "Disclosure headings carry counts so a reviewer can see whether each list contains entries before expanding it. Read every nonzero list. Sources preserve the identifiers returned with this run; they document where the record says its basis came from, but they do not certify the selected procedure or the entered values. Assumptions and deferred items are equally part of the calculation record and should remain attached when the result is shared.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "The workspace factor sentence is inaccurate for snow",
      body: "The visible source-contract sentence says **Results remain nominal and unfactored** on every manual sheet. It describes nine calculators, but it is inaccurate for snow. Snow reports a reliability-targeted basis with LRFD 1.0 and ASD 0.7. Read the returned basis and keep this interface mismatch attached to any snow review.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "The clean DG-1 source figure is not representative",
      body: "DG-1 returns a standard and a manual-input identifier, but source formats are inconsistent across the load families. Some results expose machine-local absolute policy or dataset paths; others return internal implementation or catalog identifiers. Those values may trace the running code, but they are not canonical, portable engineering citations. The result contract must separate portable citations from implementation trace before publication.",
    },
    {
      kind: "paragraph",
      text: "Load sheets intentionally have no pass/fail ratio, DCR, member combination envelope, or support-reaction packet. A calculated load record may be printed. For a sheet with no authoritative result, export preflight issues a warning and permits a clearly labeled Draft when **Allow clearly labeled draft load sheets** is on; it raises a blocking error when that option is off. The sheet's general update timestamp is not labeled as a computed-at timestamp, so do not use it as proof of when the displayed result was run.",
    },

    { kind: "related", topics: ["load-calcs", "wall-assembly", "linking-reactions"] },
  ],
};
